import { zodResolver } from '@hookform/resolvers/zod';
import {
  DiscountApplicationMethodEnum,
  IQuoteOfferingRespSchema,
} from '@monetize/types/app';
import { formatCurrency, roundNumberToDecimal } from '@monetize/utils/core';
import isNaN from 'lodash/isNaN';
import { useEffect, useMemo, useState } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  MBox,
  MButton,
  MCenterModal,
  MFlex,
  MFormField,
  MGrid,
  MRadio,
  MRadioGroup,
  MStack,
  MText,
} from '../../../../../components/Monetize';
import { MCurrencyInput } from '../../../../../components/Monetize/MCurrencyInput';
import { calculateOfferingCustomDiscount } from '../../../../../utils/discounts';
import { QuoteOfferingCustomDiscountPreview } from './QuoteOfferingCustomDiscountPreview';

export interface DiscountTargetPriceModalProps {
  quoteOffering: IQuoteOfferingRespSchema;
  eligibleItemsForDiscount?: IQuoteOfferingRespSchema['items'];
  targetAmountOptions: {
    currency: string;
    discountApplicationMethod?: DiscountApplicationMethodEnum | null;
  };
  isOpen: boolean;
  onClose: () => void;
  onChangeValue?: (discountPercentage: number) => void;
}

export const CustomDiscountModalSchema = z.object({
  scope: z.enum(['DISCOUNT_PERCENTAGE', 'TOTAL_AMOUNT']),
  customDiscountPercentage: z.number().min(0),
  targetAmount: z.number().min(0),
});

export type CustomDiscountModal = z.infer<typeof CustomDiscountModalSchema>;

const QuoteOfferingCustomDiscountModal = ({
  quoteOffering,
  eligibleItemsForDiscount,
  targetAmountOptions: { currency, discountApplicationMethod },
  isOpen,
  onClose,
  onChangeValue,
}: DiscountTargetPriceModalProps) => {
  const isSequential =
    discountApplicationMethod === DiscountApplicationMethodEnum.SEQUENTIAL;
  const [discount, setDiscount] = useState(0);
  const [editMode, setEditMode] = useState(false);

  // Calculate amounts from eligible items only
  const amountBeforeDiscount = useMemo(() => {
    if (!eligibleItemsForDiscount || eligibleItemsForDiscount.length === 0) {
      return 0;
    }
    return eligibleItemsForDiscount.reduce(
      (sum, item) => sum + (item?.amountWithoutDiscount ?? 0),
      0,
    );
  }, [eligibleItemsForDiscount]);

  const amountAfterDiscount = useMemo(() => {
    if (!eligibleItemsForDiscount || eligibleItemsForDiscount.length === 0) {
      return 0;
    }
    return eligibleItemsForDiscount.reduce(
      (sum, item) => sum + (item?.amount ?? 0),
      0,
    );
  }, [eligibleItemsForDiscount]);

  const weightedTotalCustomDiscount = useMemo(() => {
    if (!amountBeforeDiscount || amountBeforeDiscount <= 0) {
      return 0;
    }
    if (!eligibleItemsForDiscount || eligibleItemsForDiscount.length === 0) {
      return 0;
    }

    const weightedSum = eligibleItemsForDiscount.reduce<number>((sum, item) => {
      const before = item?.amountWithoutDiscount ?? 0;
      const customDiscount = item?.customDiscountAmountOrPercent ?? 0;
      return sum + before * customDiscount;
    }, 0);

    return roundNumberToDecimal(weightedSum / amountBeforeDiscount, 10);
  }, [eligibleItemsForDiscount, amountBeforeDiscount]);

  const weightedTotalCatalogDiscount = useMemo(() => {
    if (!amountBeforeDiscount || amountBeforeDiscount <= 0) {
      return 0;
    }
    if (!eligibleItemsForDiscount || eligibleItemsForDiscount.length === 0) {
      return 0;
    }

    const weightedSum = eligibleItemsForDiscount.reduce<number>((sum, item) => {
      const before = Number(item?.amountWithoutDiscount ?? 0);
      const catalogDiscount = item?.discounts?.reduce(
        (acc, currentDiscount) =>
          acc + (currentDiscount.discountAmountOrPercent ?? 0),
        0,
      );
      return sum + before * (catalogDiscount ?? 0);
    }, 0);

    return roundNumberToDecimal(weightedSum / amountBeforeDiscount, 10);
  }, [eligibleItemsForDiscount, amountBeforeDiscount]);

  const hasOfferingLevelDiscount = weightedTotalCatalogDiscount > 0;

  const amountAfterCatalogDiscount = hasOfferingLevelDiscount
    ? roundNumberToDecimal(
        amountBeforeDiscount * (1 - weightedTotalCatalogDiscount / 100),
        2,
      )
    : amountBeforeDiscount;

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    watch,
    clearErrors,
    setValue,
    trigger,
  } = useForm<CustomDiscountModal>({
    resolver: zodResolver(
      CustomDiscountModalSchema.extend({
        targetAmount: z
          .union([z.string(), z.number()])
          .transform((value, ctx) => {
            const floatNum = parseFloat(value.toString());
            if (isNaN(floatNum)) {
              ctx.addIssue({
                code: 'custom',
                message: 'Enter a number',
              });
              return z.NEVER;
            }

            if (floatNum < 0) {
              ctx.addIssue({
                code: 'custom',
                message: 'Number must be greater than or equal to 0',
              });
            }

            if (floatNum > amountAfterCatalogDiscount) {
              ctx.addIssue({
                code: 'custom',
                message: `Target Amount must be less than or equal to discounted Amount of ${formatCurrency(
                  amountAfterCatalogDiscount,
                  { currency, maximumFractionDigits: 2 },
                )}`,
              });
            }

            if (floatNum > amountBeforeDiscount) {
              ctx.addIssue({
                code: 'custom',
                message: `Target Amount must be less than or equal to un-discounted Amount of ${formatCurrency(
                  amountBeforeDiscount,
                  { currency, maximumFractionDigits: 2 },
                )}`,
              });
            }

            return floatNum;
          }),
        customDiscountPercentage: z
          .union([z.string(), z.number()])
          .transform((value, ctx) => {
            const floatNum = parseFloat(value.toString());
            if (isNaN(floatNum)) {
              ctx.addIssue({
                code: 'custom',
                message: 'Enter a number',
              });
              return z.NEVER;
            }
            if (floatNum < 0) {
              ctx.addIssue({
                code: 'custom',
                message: 'Number must be greater than or equal to 0',
              });
            }
            if (floatNum > 100) {
              ctx.addIssue({
                code: 'custom',
                message: 'Number must be less than or equal to 100',
              });
            }
            return floatNum;
          }),
      }),
    ) as Resolver<CustomDiscountModal>,
    mode: 'onChange',
    defaultValues: {
      scope: 'TOTAL_AMOUNT',
      targetAmount: roundNumberToDecimal(amountAfterDiscount),
      customDiscountPercentage:
        weightedTotalCustomDiscount ?? quoteOffering.discountPercent,
    },
  });

  const watchScope = watch('scope');
  const watchAmount = watch('targetAmount');
  const watchAmountValue = Number(watchAmount) || 0;

  const totalDiscount = roundNumberToDecimal(
    isSequential
      ? (1 - (1 - discount / 100) * (1 - weightedTotalCatalogDiscount / 100)) *
          100
      : Math.min(discount + weightedTotalCatalogDiscount, 100),
    10,
  );

  const amountAfterCalculatingDiscount =
    watchAmountValue > 0
      ? roundNumberToDecimal(
          amountBeforeDiscount * (1 - totalDiscount / 100),
          2,
        )
      : watchAmountValue;

  const displayAmount = !editMode
    ? amountAfterDiscount
    : amountAfterCalculatingDiscount || amountAfterCatalogDiscount;

  useEffect(() => {
    setDiscount(weightedTotalCustomDiscount);

    if (watchScope === 'DISCOUNT_PERCENTAGE') {
      setValue(
        'customDiscountPercentage',
        weightedTotalCustomDiscount || quoteOffering.discountPercent,
      );
      setValue('targetAmount', roundNumberToDecimal(amountAfterDiscount));
      setEditMode(false);
      trigger(['customDiscountPercentage', 'targetAmount']);
    } else if (watchScope === 'TOTAL_AMOUNT') {
      setValue('targetAmount', roundNumberToDecimal(amountAfterDiscount));
      setValue(
        'customDiscountPercentage',
        weightedTotalCustomDiscount || quoteOffering.discountPercent,
      );
      setEditMode(false);
      trigger(['customDiscountPercentage', 'targetAmount']);
    }
  }, [
    watchScope,
    clearErrors,
    setValue,
    trigger,
    amountAfterDiscount,
    weightedTotalCustomDiscount,
    quoteOffering.discountPercent,
  ]);

  function calculateAmount(newValue: number) {
    setDiscount(
      calculateOfferingCustomDiscount({
        newValue,
        isSequential,
        hasOfferingLevelDiscount,
        amountBeforeDiscount,
        amountAfterCatalogDiscount,
        weightedTotalCatalogDiscount,
      }),
    );
  }

  const onSubmit = async () => {
    onChangeValue && onChangeValue(discount);
    onClose();
  };

  const templateColumns = hasOfferingLevelDiscount
    ? '1fr 1fr 1fr 1fr 1fr'
    : '1fr 1fr 1fr 1fr';
  const columnGap = hasOfferingLevelDiscount ? 3 : 4;

  return (
    <MCenterModal
      size={hasOfferingLevelDiscount ? '4xl' : 'xl'}
      isOpen={isOpen}
      onClose={onClose}
      modalTitle="Apply Discounts"
      closeOnOverlayClick={false}
      closeOnEsc={false}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          <MButton onClick={onClose} variant="cancel" minW="auto">
            Cancel
          </MButton>
          <MButton
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            isDisabled={!isValid}
            type="submit"
            minW="auto"
          >
            Save
          </MButton>
        </MStack>
      )}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <MFlex flexDir="column" gap={4}>
          <MText>
            Apply a discount to all products within this offering using a
            discount percentage or by targeting a total amount.
          </MText>

          <Controller
            control={control}
            name="scope"
            render={({ field: { value, onChange, ...rest } }) => (
              <MRadioGroup onChange={onChange} value={value} {...rest}>
                <MStack gap={4} direction="column" color="tGray.darkPurple">
                  <MRadio value="TOTAL_AMOUNT">
                    <MText display="inline-flex" alignItems="center">
                      Target Total Amount
                    </MText>
                  </MRadio>
                  <MRadio value="DISCOUNT_PERCENTAGE">
                    <MText display="inline-flex" alignItems="center">
                      Discount Percentage
                    </MText>
                  </MRadio>
                </MStack>
              </MRadioGroup>
            )}
          />

          {watchScope === 'DISCOUNT_PERCENTAGE' && (
            <MFormField
              label="Discount Percentage"
              maxWidth={'135px'}
              isInvalid={!!errors.customDiscountPercentage?.message}
            >
              <Controller
                name="customDiscountPercentage"
                control={control}
                render={({ field: { onChange, ...rest } }) => {
                  return (
                    <MCurrencyInput
                      inputMode="decimal"
                      onChange={(valueAsString, valueAsNumber) => {
                        onChange(valueAsString);
                        setDiscount(valueAsNumber);
                        setEditMode(true);
                      }}
                      {...rest}
                    />
                  );
                }}
              />
            </MFormField>
          )}
          {watchScope === 'TOTAL_AMOUNT' && (
            <MGrid templateColumns={templateColumns} gap={columnGap}>
              <MFormField
                label="Target Amount"
                isInvalid={!!errors.targetAmount?.message}
              >
                <Controller
                  name="targetAmount"
                  control={control}
                  render={({ field: { onChange, ...rest } }) => (
                    <MCurrencyInput
                      w={120}
                      inputMode="decimal"
                      precision={2}
                      onChange={(
                        valueAsString: string,
                        valueAsNumber: number,
                      ) => {
                        onChange(valueAsString);
                        setEditMode(true);
                        calculateAmount(valueAsNumber);
                      }}
                      {...rest}
                    />
                  )}
                />
              </MFormField>
              <MFormField label="Amount Before Discount">
                <MText mt={1}>
                  {formatCurrency(amountBeforeDiscount, {
                    currency,
                    maximumFractionDigits: 2,
                  })}
                </MText>
              </MFormField>
              {hasOfferingLevelDiscount ? (
                <>
                  <MFormField label="Custom Discount" maxWidth={'120px'}>
                    <MText mt={1} fontWeight={500}>
                      {roundNumberToDecimal(discount, 10)}%
                    </MText>
                  </MFormField>
                  <MFormField label="Total Discount" maxWidth={'120px'}>
                    <MText mt={1} fontWeight={500}>
                      {totalDiscount}%
                    </MText>
                  </MFormField>
                </>
              ) : (
                <MFormField label="Discount" maxWidth={'120px'}>
                  <MText mt={1} fontWeight={500}>
                    {roundNumberToDecimal(
                      editMode ? discount : quoteOffering.discountPercent,
                      10,
                    )}
                    %
                  </MText>
                </MFormField>
              )}

              <MFormField label="Amount After Discount">
                <MText mt={1} color="tPurple.safety">
                  {formatCurrency(displayAmount, {
                    currency,
                    maximumFractionDigits: 2,
                  })}
                  {' *'}
                </MText>
              </MFormField>
            </MGrid>
          )}

          <MBox minH="42px" mt={-2}>
            <MText color="tRed.base" fontSize="sm">
              {watchScope === 'TOTAL_AMOUNT' && errors?.targetAmount?.message}
            </MText>
            <MText color="tRed.base" fontSize="sm">
              {watchScope === 'DISCOUNT_PERCENTAGE' &&
                errors?.customDiscountPercentage?.message}
            </MText>
          </MBox>
        </MFlex>
      </form>

      {eligibleItemsForDiscount && eligibleItemsForDiscount.length > 0 && (
        <QuoteOfferingCustomDiscountPreview
          quoteItems={eligibleItemsForDiscount}
          discountPercentage={discount}
          currency={currency}
          editMode={editMode}
          hasOfferingLevelDiscount={hasOfferingLevelDiscount}
          templateColumns={templateColumns}
          columnGap={columnGap}
          discountApplicationMethod={discountApplicationMethod}
        />
      )}

      <MText color="tOrange.tangerine" fontSize="sm">
        * Upon Save, the Amount After Discount may be adjusted slightly to
        create a billing schedule with equal payments.
      </MText>
    </MCenterModal>
  );
};

export default QuoteOfferingCustomDiscountModal;
